import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useCsvActions } from '../context/CsvActionsContext';
import { parseCsv } from '../utils/csvParser';
import { useT } from '../i18n';
import { Capability, Resource, RESOURCE_TYPES, RESOURCE_FUNCTIONS, DOMAIN_CAT } from '../types';
import { Link, X, RotateCcw, Check } from 'lucide-react';

const NATURE_ORDER: Capability['nature'][] = [
  'Assess',
  'Decide/Design',
  'Deliver/Execute',
  'Communicate/Report',
  'Relate',
  'Improve',
];

const DOMAIN_ORDER: Capability['domain'][] = [
  'Participant',
  'Mandant',
  'Animateur',
  'Organisation',
  'External Environment',
];

interface ConsensusBreakdown {
  [consensusLevel: number]: {
    total: number;
    selected: number;
    deselected: number;
  };
}

interface CapabilityWithStats extends Capability {
  totalResources: number;
  consensusBreakdown: ConsensusBreakdown;
  totalUsers: number;
  qualifiedCount: number;
  disqualifiedCount: number;
  isQualified: boolean;
}

interface LinkedResource extends Resource {
  consensusCount: number;
  contributors: Array<{ id: number; name: string; initials: string }>;
}

interface ResourceSelection {
  resourceId: number;
  selected: boolean;
}

// Resource Insights Tooltip — shows cross-capability presence on dot hover
interface ResourceInsights {
  capabilityCount: number;
  totalCapabilities: number;
  domainSpread: number;
  natureSpread: number;
  domains: string[];
  natures: string[];
  totalUsers: number;
  avgConsensus: number;
  capabilities: Array<{ id: number; title: string; domain: string; nature: string; consensusCount: number }>;
}

function ResourceInsightsTooltip({
  resourceId,
  resourceTitle,
  anchorRect,
  pinned,
  onClose,
}: {
  resourceId: number;
  resourceTitle: string;
  anchorRect: DOMRect;
  pinned: boolean;
  onClose: () => void;
}) {
  const { t } = useT();
  const [data, setData] = useState<ResourceInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.get(`/link-qualifications/resource/${resourceId}/insights`)
      .then((d: any) => setData(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [resourceId]);

  // Click-outside to close when pinned
  useEffect(() => {
    if (!pinned) return;
    const handle = (e: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [pinned, onClose]);

  // Position to the left of the dot (since panel is on right side of screen)
  const top = Math.min(anchorRect.top, window.innerHeight - 340);
  const left = anchorRect.left - 310;

  const totalDomains = DOMAIN_ORDER.length;
  const totalNatures = NATURE_ORDER.length;

  return (
    <div
      className="resource-insights-tooltip"
      ref={tooltipRef}
      style={{ top, left: Math.max(8, left) }}
      onMouseEnter={() => {/* keep open on hover */}}
      onMouseLeave={() => { if (!pinned) onClose(); }}
    >
      <div className="rit-header">
        <div className="rit-title">{resourceTitle}</div>
      </div>
      {loading ? (
        <div className="rit-loading">{t('lq.insights.loading')}</div>
      ) : data ? (
        <div className="rit-body">
          {/* Key stats row */}
          <div className="rit-stats">
            <div className="rit-stat">
              <div className="rit-stat-value">{data.capabilityCount}</div>
              <div className="rit-stat-label">{t('lq.insights.capabilities')}</div>
              <div className="rit-stat-total">{t('lq.insights.of').replace('{n}', String(data.totalCapabilities))}</div>
            </div>
            <div className="rit-stat">
              <div className="rit-stat-value">{data.domainSpread}</div>
              <div className="rit-stat-label">{t('lq.insights.domains')}</div>
              <div className="rit-stat-total">{t('lq.insights.of').replace('{n}', String(totalDomains))}</div>
            </div>
            <div className="rit-stat">
              <div className="rit-stat-value">{data.natureSpread}</div>
              <div className="rit-stat-label">{t('lq.insights.natures')}</div>
              <div className="rit-stat-total">{t('lq.insights.of').replace('{n}', String(totalNatures))}</div>
            </div>
            <div className="rit-stat">
              <div className="rit-stat-value">{data.avgConsensus}/{data.totalUsers}</div>
              <div className="rit-stat-label">{t('lq.insights.avgConsensus')}</div>
            </div>
          </div>

          {/* Domain and nature tags */}
          <div className="rit-tags">
            {data.domains.map(d => (
              <span key={d} className="rit-tag rit-tag-domain">{d}</span>
            ))}
            {data.natures.map(n => (
              <span key={n} className="rit-tag rit-tag-nature">{n}</span>
            ))}
          </div>

          {/* Capability list */}
          <div className="rit-cap-list">
            {data.capabilities.map(c => (
              <div key={c.id} className="rit-cap-row">
                <div className="rit-cap-name">{c.title}</div>
                <div className="rit-cap-consensus">{c.consensusCount}/{data.totalUsers}</div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="rit-loading">{t('lq.insights.noData')}</div>
      )}
    </div>
  );
}

// Detail Panel Component (slide-in from right)
function DetailPanel({
  capability,
  onClose,
}: {
  capability: CapabilityWithStats;
  onClose: () => void;
}) {
  const { t } = useT();
  const [resources, setResources] = useState<LinkedResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedResourceId, setExpandedResourceId] = useState<number | null>(null);
  const [insightsResource, setInsightsResource] = useState<{ id: number; title: string; rect: DOMRect; pinned: boolean } | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadResources = async () => {
      try {
        const data = await api.get(`/link-qualifications/capability/${capability.id}/resources`) as any[];
        // API returns snake_case, map to camelCase
        setResources(data.map(r => ({
          ...r,
          consensusCount: r.consensus_count ?? r.consensusCount,
        })));
      } catch (err) {
        console.error('Failed to load resources:', err);
      } finally {
        setLoading(false);
      }
    };
    loadResources();
  }, [capability.id]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Close expanded resource description on click-away
  useEffect(() => {
    if (expandedResourceId === null) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.rl-item-expandable')) {
        setExpandedResourceId(null);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [expandedResourceId]);

  // Consensus breakdown rows
  const consColors = ['var(--cons-all)', 'var(--cons-most)', 'var(--cons-some)', 'var(--cons-few)', 'var(--cons-one)'];
  const breakdownRows: Array<{ k: number; count: number; label: string; color: string }> = [];
  let totalRes = 0;

  for (let k = capability.totalUsers; k >= 1; k--) {
    const bin = capability.consensusBreakdown[k];
    if (bin && bin.total > 0) {
      const tier = capability.totalUsers - k;
      const color = consColors[Math.min(tier, consColors.length - 1)];
      const label = k === capability.totalUsers
        ? t('lq.raters.all').replace('{n}', String(k))
        : k === 1 ? t('lq.raters.one') : t('lq.raters.some').replace('{k}', String(k)).replace('{total}', String(capability.totalUsers));
      breakdownRows.push({ k, count: bin.total, label, color });
      totalRes += bin.total;
    }
  }

  // Group resources by consensus tier
  const resourcesByTier: Array<{ k: number; label: string; color: string; items: LinkedResource[] }> = [];
  for (let k = capability.totalUsers; k >= 1; k--) {
    const tier = capability.totalUsers - k;
    const color = consColors[Math.min(tier, consColors.length - 1)];
    const label = k === capability.totalUsers
      ? t('lq.raters.all').replace('{n}', String(k))
      : k === 1 ? t('lq.raters.one') : t('lq.raters.some').replace('{k}', String(k)).replace('{total}', String(capability.totalUsers));
    const items = resources.filter(r => r.consensusCount === k);
    if (items.length > 0) {
      resourcesByTier.push({ k, label, color, items });
    }
  }

  return (
    <div className="panel-slide panel-slide--standalone open" ref={panelRef}>
      <div className="panel-slide-head">
        <div className="panel-slide-title">{capability.title}</div>
        <button className="panel-slide-close" onClick={onClose}><X className="icon-sm" /></button>
      </div>
      <div className="panel-slide-body">
        {/* Meta tags */}
        <div className="detail-card-header-tags" style={{ marginBottom: 'var(--s12)' }}>
          <span className={`meta-tag meta-tag--cat-${DOMAIN_CAT[capability.domain] || 1}`}>{capability.domain}</span>
          <span className="meta-tag">{capability.nature}</span>
        </div>
        <div style={{ display: 'flex', gap: 'var(--s12)', fontSize: 'var(--t-12)', fontFamily: 'var(--font-mono)', color: 'var(--ink-3)', marginBottom: 'var(--s16)' }}>
          <span>{t(capability.totalResources !== 1 ? 'lq.resourcesLinked.many' : 'lq.resourcesLinked.one').replace('{n}', String(capability.totalResources))}</span>
          <span>{t(capability.totalUsers !== 1 ? 'lq.ratersCount.many' : 'lq.ratersCount.one').replace('{n}', String(capability.totalUsers))}</span>
        </div>

        {/* Description */}
        <div className="detail-desc">
          {capability.description || t('lq.noDescription')}
        </div>

        {/* Consensus Breakdown */}
        <div className="detail-section-label">{t('lq.consensusBreakdown')}</div>
        <div className="resource-breakdown">
          {breakdownRows.map(row => {
            const pct = totalRes > 0 ? Math.round((row.count / totalRes) * 100) : 0;
            return (
              <div key={row.k} className="rb-row">
                <div className="rb-dot" style={{ background: row.color }} />
                <div className="rb-type">{row.label}</div>
                <div className="rb-count">{row.count}</div>
                <div className="rb-bar-wrap">
                  <div className="rb-bar-fill" style={{ width: `${pct}%`, background: row.color }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Linked Resources by tier */}
        {loading ? (
          <div style={{ padding: 'var(--s16)', textAlign: 'center', color: 'var(--ink-3)' }}>{t('lq.loadingResources')}</div>
        ) : (
          <div className="resource-list">
            <div className="detail-section-label" style={{ marginTop: 'var(--s20)' }}>{t('lq.linkedResources')}</div>
            {resourcesByTier.map(tier => (
              <React.Fragment key={tier.k}>
                <div className="rl-group-label">{tier.label} — {t(tier.items.length !== 1 ? 'lq.resourceCount.many' : 'lq.resourceCount.one').replace('{n}', String(tier.items.length))}</div>
                {tier.items.map(r => {
                  const contribs = r.contributors && r.contributors.length > 0
                    ? [...new Set(r.contributors.map(c => c.initials))].join(', ')
                    : '';
                  const isExpanded = expandedResourceId === r.id;
                  return (
                    <div key={r.id} className={`rl-item rl-item-expandable ${isExpanded ? 'expanded' : ''}`}>
                      <div
                        className="rl-item-dot clickable"
                        style={{ background: tier.color }}
                        onMouseEnter={(e) => {
                          if (insightsResource?.pinned) return;
                          const rect = (e.target as HTMLElement).getBoundingClientRect();
                          setInsightsResource({ id: r.id, title: r.title, rect, pinned: false });
                        }}
                        onMouseLeave={() => { if (!insightsResource?.pinned) setInsightsResource(null); }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const rect = (e.target as HTMLElement).getBoundingClientRect();
                          setInsightsResource(
                            insightsResource?.pinned && insightsResource.id === r.id
                              ? null
                              : { id: r.id, title: r.title, rect, pinned: true }
                          );
                        }}
                      />
                      <div className="rl-item-body">
                        <div
                          className="rl-item-name clickable"
                          onClick={(e) => { e.stopPropagation(); setExpandedResourceId(isExpanded ? null : r.id); }}
                        >
                          {r.title}
                        </div>
                        {isExpanded && (r as any).description && (
                          <div className="rl-item-desc">{(r as any).description}</div>
                        )}
                        {isExpanded && !(r as any).description && (
                          <div className="rl-item-desc" style={{ fontStyle: 'italic' }}>{t('lq.noDescription')}</div>
                        )}
                      </div>
                      <div className="rl-item-fn">{contribs}</div>
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
            {resources.length === 0 && (
              <div style={{ color: 'var(--ink-3)', fontStyle: 'italic', padding: '8px 0' }}>{t('lq.noResourcesLinkedYet')}</div>
            )}
          </div>
        )}
      </div>

      {/* Resource Insights Tooltip — portal to escape transform containing block */}
      {insightsResource && createPortal(
        <ResourceInsightsTooltip
          resourceId={insightsResource.id}
          resourceTitle={insightsResource.title}
          anchorRect={insightsResource.rect}
          pinned={insightsResource.pinned}
          onClose={() => setInsightsResource(null)}
        />,
        document.body
      )}
    </div>
  );
}

// Resource Card Component for Qualification Modal — matches LinkingPage aggregate view
function QualificationResourceCard({
  resource,
  isSelected,
  onToggle,
  isAdmin,
}: {
  resource: LinkedResource;
  isSelected: boolean;
  onToggle: () => void;
  isAdmin: boolean;
}) {
  const { t } = useT();
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const contributorInitials = resource.contributors && resource.contributors.length > 0
    ? [...new Set(resource.contributors.map(c => c.initials))].join(' | ')
    : null;

  const selectedClass = isSelected ? `selected selected-${resource.type?.toLowerCase() || 'aggregate'}` : '';
  const resourceDescription = (resource as any).description || null;

  const handleShowTooltip = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      // Position tooltip below and to the left of the button
      setTooltipPos({
        top: rect.bottom + 6,
        left: Math.max(8, rect.right - 260), // 260 = tooltip width + some margin
      });
    }
    setShowTooltip(true);
  };

  const handleHideTooltip = () => {
    setShowTooltip(false);
    setTooltipPos(null);
  };

  return (
    <div
      className={`resource-card ${selectedClass} aggregate-view`}
      onClick={isAdmin ? onToggle : undefined}
      style={{ cursor: isAdmin ? 'pointer' : 'default', opacity: isSelected ? 1 : 0.65, position: 'relative' }}
    >
      {/* Info button in top-right corner */}
      {resourceDescription && (
        <button
          ref={buttonRef}
          className="resource-card-info"
          onClick={(e) => {
            e.stopPropagation();
            if (showTooltip) handleHideTooltip();
            else handleShowTooltip();
          }}
          onMouseEnter={handleShowTooltip}
          onMouseLeave={handleHideTooltip}
          title={t('lq.showDescription')}
        >
          i
        </button>
      )}
      {/* Tooltip rendered via portal to escape stacking context */}
      {showTooltip && resourceDescription && tooltipPos && createPortal(
        <div
          className="resource-card-tooltip-portal"
          style={{ top: tooltipPos.top, left: tooltipPos.left }}
        >
          <div className="resource-card-tooltip-title">{resource.title}</div>
          <div className="resource-card-tooltip-desc">{resourceDescription}</div>
        </div>,
        document.body
      )}
      <div className="resource-card-title">{resource.title}</div>
      {contributorInitials && (
        <div className="resource-card-contributors">{contributorInitials}</div>
      )}
    </div>
  );
}

// Resource Grid Modal Component
function ResourceGridModal({
  capability,
  onClose,
  isAdmin,
}: {
  capability: CapabilityWithStats;
  onClose: () => void;
  isAdmin: boolean;
}) {
  const { t } = useT();
  const [resources, setResources] = useState<LinkedResource[]>([]);
  const [selections, setSelections] = useState<Map<number, boolean>>(new Map());
  const [consensusThreshold, setConsensusThreshold] = useState<number>(capability.totalUsers);
  const [sliderActive, setSliderActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [capability.id]);

  const loadData = async () => {
    try {
      const resourcesData = await api.get(`/link-qualifications/capability/${capability.id}/resources`) as any[];
      setResources(resourcesData.map(r => ({
        ...r,
        consensusCount: r.consensus_count ?? r.consensusCount,
      })));

      const selectionsData = await api.get(`/link-qualifications/capability/${capability.id}/selections`) as Array<{ resource_id: number; selected: number }>;
      const selectionsMap = new Map<number, boolean>();
      selectionsData.forEach(s => selectionsMap.set(s.resource_id, s.selected === 1));
      setSelections(selectionsMap);
    } catch (err) {
      console.error('Failed to load resources:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!isAdmin) return;

    setSaving(true);
    try {
      const selectionsArray: ResourceSelection[] = [];
      selections.forEach((selected, resourceId) => {
        selectionsArray.push({ resourceId, selected });
      });

      await api.put(`/link-qualifications/capability/${capability.id}/selections`, {
        selections: selectionsArray,
      });

      onClose();
    } catch (err) {
      console.error('Failed to save selections:', err);
      alert(t('lq.alert.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleSliderChange = (threshold: number) => {
    if (!isAdmin) return;
    setConsensusThreshold(threshold);
    setSliderActive(true);

    // Select all resources with consensusCount >= threshold, deselect the rest
    const newSelections = new Map<number, boolean>();
    resources.forEach(resource => {
      newSelections.set(resource.id, resource.consensusCount >= threshold);
    });
    setSelections(newSelections);
  };

  const toggleResource = (resourceId: number) => {
    if (!isAdmin) return;

    // Manual override → grey out slider
    setSliderActive(false);

    const newSelections = new Map(selections);
    const currentValue = newSelections.get(resourceId) || false;
    newSelections.set(resourceId, !currentValue);
    setSelections(newSelections);
  };

  // Organize resources into grid structure
  const resourceGrid: Record<string, Record<string, LinkedResource[]>> = {};
  const unclassifiedResources: LinkedResource[] = [];

  // Initialize grid
  for (const fn of RESOURCE_FUNCTIONS) {
    resourceGrid[fn] = {};
    for (const type of RESOURCE_TYPES) {
      resourceGrid[fn][type] = [];
    }
  }

  // Populate grid - collect unclassified resources separately
  for (const resource of resources) {
    const fn = resource.function;
    const type = resource.type;
    if (resourceGrid[fn] && resourceGrid[fn][type]) {
      resourceGrid[fn][type].push(resource);
    } else {
      // Resource has null/missing/invalid function or type
      unclassifiedResources.push(resource);
    }
  }

  // Check if any row has resources
  const hasResourcesInRow = (fn: string): boolean => {
    return RESOURCE_TYPES.some(type => resourceGrid[fn]?.[type]?.length > 0);
  };

  const selectedCount = Array.from(selections.values()).filter(v => v).length;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-full" onClick={e => e.stopPropagation()}>
        {/* Capability Card Header */}
        <div className="detail-card" style={{ margin: 0, borderRadius: 'var(--r2) var(--r2) 0 0', borderBottom: '1px solid var(--line)' }}>
          <button className="btn-icon" onClick={onClose} style={{ position: 'absolute', top: 'var(--s16)', right: 'var(--s16)' }}><X className="icon-sm" /></button>
          <div className="detail-card-header">
            <h4 className="detail-card-title">{capability.title}</h4>
            <div className="detail-card-header-tags" style={{ marginRight: '40px' }}>
              <span className={`meta-tag meta-tag--cat-${DOMAIN_CAT[capability.domain] || 1}`}>{capability.domain}</span>
              <span className="meta-tag">{capability.nature}</span>
            </div>
          </div>
          <p className="detail-card-description" style={{ maxWidth: Math.min(900, Math.max(300, Math.ceil((capability.description || '').length / 3 * 7))) }}>
            {capability.description || t('lq.noDescription')}
          </p>
          <div className="qual-threshold-meta-row">
            <div className={`qual-threshold qual-threshold--inline ${!sliderActive ? 'inactive' : ''} ${!isAdmin ? 'view-only' : ''}`} style={{ maxWidth: 600 }}>
              <div className="qual-threshold-row">
                <label className="qual-threshold-label">{t('lq.consensusThreshold').replace('{n}', String(consensusThreshold)).replace('{total}', String(capability.totalUsers))}</label>
                <input
                  type="range"
                  min="1"
                  max={capability.totalUsers}
                  value={consensusThreshold}
                  onChange={e => handleSliderChange(parseInt(e.target.value))}
                  disabled={!isAdmin}
                  className="qual-threshold-slider"
                />
                <span className="qual-threshold-count">{t('lq.selectedShort').replace('{n}', String(selectedCount))}</span>
                {isAdmin && (
                  <button
                    className="btn-icon"
                    title={t('lq.resetSelections')}
                    onClick={() => {
                      setSelections(new Map());
                      setSliderActive(true);
                      setConsensusThreshold(capability.totalUsers);
                    }}
                    style={{ flexShrink: 0 }}
                  >
                    <RotateCcw className="icon-sm" />
                  </button>
                )}
              </div>
              {!sliderActive && isAdmin && (
                <div className="qual-threshold-hint">{t('lq.manualOverride')}</div>
              )}
              {!isAdmin && (
                <div className="qual-threshold-hint">{t('lq.viewOnly')}</div>
              )}
            </div>
            <span className="detail-card-meta">
              {t(capability.totalResources !== 1 ? 'lq.resourcesLinked.many' : 'lq.resourcesLinked.one').replace('{n}', String(capability.totalResources))}{' · '}{t(capability.totalUsers !== 1 ? 'lq.ratersCount.many' : 'lq.ratersCount.one').replace('{n}', String(capability.totalUsers))}
            </span>
          </div>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="flex items-center justify-center" style={{ height: 200 }}>
              <div className="spinner" />
            </div>
          ) : (
            <>

              {/* Resource Grid */}
              {resources.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon"><Link /></div>
                  <div className="empty-state-title">{t('lq.emptyState.title')}</div>
                  <div className="empty-state-desc">{t('lq.emptyState.desc')}</div>
                </div>
              ) : (
                <>
                  <div className="matrix-grid-container">
                    <div
                      className="matrix-grid"
                      style={{ '--matrix-cols': '48px repeat(4, minmax(190px, 1fr))' } as React.CSSProperties}
                    >
                      {/* Header Row */}
                      <div className="matrix-grid-corner"></div>
                      {RESOURCE_TYPES.map((type) => {
                        // Split after first word for better readability
                        const words = type.split(' ');
                        const firstWord = words[0];
                        const rest = words.slice(1).join(' ');
                        return (
                          <div key={type} className="matrix-grid-col">
                            {firstWord}{rest && <><br />{rest}</>}
                          </div>
                        );
                      })}

                      {/* Data Rows */}
                      {RESOURCE_FUNCTIONS.map((fn) => {
                        if (!hasResourcesInRow(fn)) return null;

                        // Split row header after first word for better readability
                        const words = fn.split(' ');
                        const firstWord = words[0];
                        const rest = words.slice(1).join(' ');

                        return (
                          <React.Fragment key={fn}>
                            <div className="matrix-grid-row">
                              {firstWord}{rest && <><br />{rest}</>}
                            </div>
                            {RESOURCE_TYPES.map((type) => (
                              <div key={`${fn}-${type}`} className="matrix-grid-cell matrix-grid-cell--wrap">
                                {resourceGrid[fn]?.[type]?.map((resource) => (
                                  <QualificationResourceCard
                                    key={resource.id}
                                    resource={resource}
                                    isSelected={selections.get(resource.id) || false}
                                    onToggle={() => toggleResource(resource.id)}
                                    isAdmin={isAdmin}
                                  />
                                ))}
                              </div>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>

                  {/* Unclassified Resources Section */}
                  {unclassifiedResources.length > 0 && (
                    <div className="unclassified-resources-section">
                      <div className="unclassified-header">
                        <span className="unclassified-label">{t('lq.unclassified.label')}</span>
                        <span className="unclassified-count">{t(unclassifiedResources.length !== 1 ? 'lq.resourceCount.many' : 'lq.resourceCount.one').replace('{n}', String(unclassifiedResources.length))}</span>
                      </div>
                      <div className="unclassified-hint">
                        {t('lq.unclassified.hint')}
                      </div>
                      <div className="unclassified-grid">
                        {unclassifiedResources.map((resource) => (
                          <QualificationResourceCard
                            key={resource.id}
                            resource={resource}
                            isSelected={selections.get(resource.id) || false}
                            onToggle={() => toggleResource(resource.id)}
                            isAdmin={isAdmin}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>{isAdmin ? t('common.cancel') : t('common.close')}</button>
          {isAdmin && (
            <button className="btn btn-accent" onClick={handleSave} disabled={saving}>
              {saving ? t('lq.saving') : t('lq.saveAndClose')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Consensus Stacked Bar Component
function ConsensusStackedBar({
  consensusBreakdown,
  totalRaters,
  height = 20,
}: {
  consensusBreakdown: ConsensusBreakdown;
  totalRaters: number;
  height?: number;
}) {
  const { t } = useT();
  // Stepped indigo consensus palette using CSS variables
  const consVars = ['var(--cons-all)', 'var(--cons-most)', 'var(--cons-some)', 'var(--cons-few)', 'var(--cons-one)'];

  const getSegmentColors = (k: number, maxK: number) => {
    // Map k (rater count) to a tier index: highest k = darkest (tier 0)
    const tier = maxK - k;
    const bg = consVars[Math.min(tier, consVars.length - 1)];
    return { bg, text: 'white' };
  };

  // Calculate total resources and segment widths
  const segments: Array<{ k: number; active: number; inactive: number; total: number }> = [];
  let totalResources = 0;

  for (let k = totalRaters; k >= 1; k--) {
    const binData = consensusBreakdown[k] || { total: 0, selected: 0, deselected: 0 };
    if (binData.total > 0) {
      segments.push({
        k,
        active: binData.selected,
        inactive: binData.deselected,
        total: binData.total
      });
      totalResources += binData.total;
    }
  }

  if (totalResources === 0) {
    return <div className="consensus-stacked-bar empty" style={{ height }}>{t('lq.noResources')}</div>;
  }

  return (
    <div className="consensus-stacked-bar" style={{ height }}>
      {segments.map(seg => {
        const widthPercent = (seg.total / totalResources) * 100;
        const colors = getSegmentColors(seg.k, totalRaters);

        return (
          <div
            key={seg.k}
            className="stacked-bar-segment"
            style={{
              width: `${widthPercent}%`,
              background: colors.bg,
              color: colors.text,
              minWidth: '24px'
            }}
            title={t('lq.bar.tooltip')
              .replace('{k}', String(seg.k))
              .replace('{active}', String(seg.active))
              .replace('{excluded}', seg.inactive > 0 ? t('lq.bar.excluded').replace('{n}', String(seg.inactive)) : '')
              .replace('{total}', String(seg.total))}
          >
            <span className="segment-count">{seg.total}</span>
          </div>
        );
      })}
    </div>
  );
}

// Capability Card Component
function CapabilityCard({
  capability,
  onClick,
  onShowInfo,
}: {
  capability: CapabilityWithStats;
  onClick: () => void;
  onShowInfo: () => void;
}) {
  const { t } = useT();
  return (
    <div className={`link-qual-capability-card ${capability.isQualified ? 'qualified' : ''}`} onClick={onClick}>
      {/* Info icon in top-right corner */}
      <button
        className="resource-card-info card-info-top-right"
        onClick={(e) => {
          e.stopPropagation();
          onShowInfo();
        }}
        title={t('lq.showDescription')}
      >
        i
      </button>

      <div className="card-title">
        {capability.title}
        {capability.isQualified && (
          <span className="qualification-indicator" title={t('lq.qualified')}><Check className="icon-sm" /></span>
        )}
      </div>

      {/* Resource count */}
      <div className="resource-count">
        {t(capability.totalResources !== 1 ? 'lq.resourceCount.many' : 'lq.resourceCount.one').replace('{n}', String(capability.totalResources))}
        {capability.qualifiedCount > 0 && (
          <span className="chosen-count">{t('lq.chosen').replace('{n}', String(capability.qualifiedCount))}</span>
        )}
      </div>

      {/* Consensus stacked bar at bottom */}
      <ConsensusStackedBar
        consensusBreakdown={capability.consensusBreakdown}
        totalRaters={capability.totalUsers}
      />
    </div>
  );
}

export function LinkQualificationPage() {
  const { t } = useT();
  const { isAdmin } = useAuth();
  const { setCsvActions } = useCsvActions();
  const [capabilities, setCapabilities] = useState<CapabilityWithStats[]>([]);
  const [selectedCapability, setSelectedCapability] = useState<CapabilityWithStats | null>(null);
  const [loading, setLoading] = useState(true);

  // Detail panel state
  const [detailCapability, setDetailCapability] = useState<CapabilityWithStats | null>(null);

  // CSV import state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);

  // Space+drag panning
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [spaceHeld, setSpaceHeld] = useState(false);
  const spaceHeldRef = useRef(false);
  const isPanning = useRef(false);
  const panStart = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });

  useEffect(() => {
    const el = gridContainerRef.current;
    if (!el) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'KeyV' && !e.repeat && !spaceHeldRef.current && !(e.target as HTMLElement).closest('input, textarea, select, [contenteditable]')) {
        spaceHeldRef.current = true;
        setSpaceHeld(true);
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'KeyV') {
        spaceHeldRef.current = false;
        isPanning.current = false;
        setSpaceHeld(false);
      }
    };
    const onMouseDown = (e: MouseEvent) => {
      if (!spaceHeldRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      isPanning.current = true;
      panStart.current = { x: e.clientX, y: e.clientY, scrollLeft: el.scrollLeft, scrollTop: el.scrollTop };
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!isPanning.current) return;
      el.scrollLeft = panStart.current.scrollLeft - (e.clientX - panStart.current.x);
      el.scrollTop = panStart.current.scrollTop - (e.clientY - panStart.current.y);
    };
    const onMouseUp = () => { isPanning.current = false; };
    const onClick = (e: MouseEvent) => {
      if (!spaceHeldRef.current) return;
      e.preventDefault();
      e.stopPropagation();
    };

    // Capture phase on window for keys to beat browser default scroll
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', onKeyUp, true);
    // Capture phase on container for mouse to beat child handlers
    el.addEventListener('mousedown', onMouseDown, true);
    el.addEventListener('mousemove', onMouseMove, true);
    el.addEventListener('mouseup', onMouseUp, true);
    el.addEventListener('mouseleave', onMouseUp);
    el.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', onKeyUp, true);
      el.removeEventListener('mousedown', onMouseDown, true);
      el.removeEventListener('mousemove', onMouseMove, true);
      el.removeEventListener('mouseup', onMouseUp, true);
      el.removeEventListener('mouseleave', onMouseUp);
      el.removeEventListener('click', onClick, true);
    };
  }, []);

  useEffect(() => {
    loadCapabilities();
  }, []);

  const loadCapabilities = async () => {
    try {
      const data = await api.get('/link-qualifications/all-capabilities') as CapabilityWithStats[];
      setCapabilities(data);
    } catch (err) {
      console.error('Failed to load capabilities:', err);
    } finally {
      setLoading(false);
    }
  };

  // Register CSV actions in topbar
  useEffect(() => {
    setCsvActions({
      uploadLabel: t('lq.csv.importLabel'),
      onUpload: isAdmin ? () => fileInputRef.current?.click() : undefined,
      downloadUrl: '/api/link-qualifications/export-csv',
      downloadLabel: t('lq.csv.exportLabel'),
      uploading: importing,
    });
    return () => setCsvActions(null);
  }, [importing, isAdmin, setCsvActions, t]);

  const handleCsvFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      const rows = parseCsv(text);
      if (rows.length === 0) {
        alert(t('lq.alert.csvEmpty'));
        return;
      }
      executeImport(rows);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const executeImport = async (rows: Record<string, string>[]) => {
    setImporting(true);
    try {
      const result = await api.importLinkQualificationsCsv(rows) as { imported: number; skipped: number };
      alert(t('lq.alert.importComplete').replace('{imported}', String(result.imported)).replace('{skipped}', String(result.skipped)));
      loadCapabilities();
    } catch (err: any) {
      alert(t('lq.alert.importFailed').replace('{msg}', err.message));
    } finally {
      setImporting(false);
    }
  };

  const handleShowInfo = (capability: CapabilityWithStats) => {
    setDetailCapability(capability);
  };

  // Page is always viewable - non-admins see read-only mode

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: 400 }}>
        <div className="spinner" />
      </div>
    );
  }

  // Organize capabilities by domain and nature - SUPPORT MULTIPLE CAPABILITIES PER CELL
  const capabilityGrid: Record<string, Record<string, CapabilityWithStats[]>> = {};
  DOMAIN_ORDER.forEach(domain => {
    capabilityGrid[domain] = {};
    NATURE_ORDER.forEach(nature => {
      capabilityGrid[domain][nature] = capabilities.filter(c => c.domain === domain && c.nature === nature);
    });
  });

  return (
    <div className="link-qual-page">
      <style>{`
        .link-qual-page {
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
        }

        /* Capability cards within grid cells */
        .link-qual-capability-card {
          background: var(--surface);
          border: 1px solid var(--line);
          border-radius: var(--r2);
          padding: var(--s16) var(--s16) var(--s12);
          cursor: pointer;
          transition: all var(--dt-mid) var(--ease);
          position: relative;
          display: flex;
          flex-direction: column;
          gap: var(--s8);
        }

        .link-qual-capability-card:hover {
          border-color: var(--line);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.04);
        }

        [data-theme="dark"] .link-qual-capability-card:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        }

        .link-qual-capability-card.qualified {
          border-color: var(--ok);
        }

        .qualification-indicator {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: var(--ok);
          color: white;
          font-size: 10px;
          font-weight: bold;
          margin-left: 6px;
          vertical-align: middle;
          flex-shrink: 0;
        }

        .link-qual-capability-card .card-info-top-right {
          position: absolute;
          top: var(--s12);
          right: var(--s12);
          width: 20px;
          height: 20px;
          border-radius: var(--r-full);
          border: 1px solid var(--line);
          background: var(--bg-inset);
          font-size: 11px;
          font-weight: 700;
          color: var(--ink-3);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all var(--dt-fast);
          opacity: 0;
        }
        .link-qual-capability-card:hover .card-info-top-right { opacity: 1; }
        .link-qual-capability-card .card-info-top-right:hover {
          background: var(--accent);
          color: white;
          border-color: var(--accent);
        }

        .link-qual-capability-card .card-title {
          font-size: var(--t-15);
          font-weight: 600;
          color: var(--ink-1);
          line-height: 1.35;
          padding-right: 24px;
          flex: 1;
        }

        .link-qual-capability-card .resource-count {
          font-size: var(--t-12);
          font-family: var(--font-mono);
          font-weight: 500;
          color: var(--ink-3);
          margin-bottom: 6px;
        }

        /* Consensus Stacked Bar */
        .consensus-stacked-bar {
          display: flex;
          width: 100%;
          height: 22px;
          border-radius: var(--r1);
          overflow: hidden;
          margin-top: auto;
          background: var(--bg-inset);
        }

        .consensus-stacked-bar.empty {
          background: var(--bg-inset);
          color: var(--ink-3);
          font-size: var(--t-12);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .stacked-bar-segment {
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          height: 100%;
          overflow: hidden;
          transition: opacity var(--dt-fast);
        }

        .segment-count {
          font-size: 0.6875rem;
          font-family: var(--font-mono);
          font-weight: 600;
          color: white;
          z-index: 1;
          white-space: nowrap;
          text-shadow: 0 1px 2px rgba(0,0,0,0.2);
          padding: 0 4px;
        }

        [data-theme="dark"] .segment-count {
          color: rgba(0,0,0,0.75);
          text-shadow: none;
        }

        .stacked-bar-segment:hover {
          opacity: 0.9;
          cursor: pointer;
        }

        /* Compact consensus threshold slider */
        .qual-threshold {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: var(--s8) var(--s12);
          background: var(--bg-inset);
          border-radius: var(--r2);
          margin-bottom: var(--s12);
          transition: opacity var(--dt-fast);
        }
        .qual-threshold.inactive {
          opacity: 0.45;
        }
        .qual-threshold-row {
          display: flex;
          align-items: center;
          gap: var(--s12);
        }
        .qual-threshold-label {
          font-size: var(--t-12);
          font-weight: 600;
          font-family: var(--font-mono);
          color: var(--ink-3);
          white-space: nowrap;
          min-width: 100px;
        }
        .qual-threshold-slider {
          flex: 1;
          height: 4px;
          -webkit-appearance: none;
          appearance: none;
          background: var(--line);
          border-radius: 2px;
          outline: none;
          cursor: pointer;
        }
        .qual-threshold-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: var(--accent);
          border: 2px solid var(--surface);
          box-shadow: 0 1px 3px rgba(0,0,0,0.15);
          cursor: pointer;
        }
        .qual-threshold-slider::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: var(--accent);
          border: 2px solid var(--surface);
          box-shadow: 0 1px 3px rgba(0,0,0,0.15);
          cursor: pointer;
        }
        .qual-threshold-count {
          font-size: var(--t-12);
          font-family: var(--font-mono);
          color: var(--ink-2);
          white-space: nowrap;
        }
        .qual-threshold-hint {
          font-size: 0.625rem;
          color: var(--ink-2);
          font-style: italic;
        }
        .qual-threshold.view-only {
          opacity: 0.7;
        }
        .qual-threshold.view-only .qual-threshold-slider {
          cursor: not-allowed;
        }

        .qual-threshold-meta-row {
          display: flex;
          align-items: flex-start;
          gap: var(--s16);
          margin-top: var(--s12);
        }
        .qual-threshold-meta-row > .detail-card-meta {
          margin-left: auto;
          align-self: center;
        }
        .qual-threshold--inline {
          flex: 1;
          min-width: 0;
          padding: 6px var(--s12);
        }

        .detail-desc {
          font-size: var(--t-15); color: var(--ink-2);
          line-height: 1.65; margin-bottom: var(--s20);
        }
        .detail-section-label {
          font-size: var(--t-12); font-weight: 600;
          color: var(--ink-3); text-transform: uppercase;
          letter-spacing: 0.05em; margin-bottom: var(--s12);
        }

        /* Consensus breakdown rows */
        .resource-breakdown {
          display: flex; flex-direction: column; gap: var(--s8);
        }
        .rb-row {
          display: flex; align-items: center; gap: var(--s12);
          padding: var(--s8) var(--s12);
          border-radius: var(--r2);
          background: var(--bg-inset);
        }
        .rb-dot { width: 10px; height: 10px; border-radius: 2px; flex-shrink: 0; }
        .rb-type { font-size: var(--t-13); font-weight: 500; flex: 1; }
        .rb-count {
          font-family: var(--font-mono); font-size: var(--t-13);
          font-weight: 500; color: var(--ink-2);
        }
        .rb-bar-wrap {
          width: 80px; height: 6px;
          background: var(--line);
          border-radius: var(--r-full);
          overflow: hidden;
        }
        .rb-bar-fill { height: 100%; border-radius: var(--r-full); transition: width 0.3s var(--ease); }

        /* Resource list in detail panel */
        .resource-list { margin-top: var(--s20); }
        .rl-group-label {
          font-size: var(--t-12); font-weight: 600;
          color: var(--ink-3); margin-bottom: var(--s8);
          margin-top: var(--s12); padding-bottom: var(--s4);
          border-bottom: 1px solid var(--line);
        }
        .rl-item {
          display: flex; align-items: flex-start; gap: var(--s8);
          padding: 6px var(--s8);
          border-radius: var(--r1);
          font-size: var(--t-13);
          transition: background var(--dt-fast);
        }
        .rl-item:hover { background: var(--bg-inset); }
        .rl-item-dot { width: 6px; height: 6px; border-radius: 1px; flex-shrink: 0; margin-top: 6px; transition: transform var(--dt-fast); }
        .rl-item-dot.clickable { cursor: pointer; width: 8px; height: 8px; margin-top: 5px; border-radius: 2px; }
        .rl-item-dot.clickable:hover { transform: scale(1.8); }
        .rl-item-body { flex: 1; min-width: 0; }
        .rl-item-name { flex: 1; }
        .rl-item-name.clickable { cursor: pointer; text-decoration: underline; text-decoration-color: var(--line); text-underline-offset: 2px; }
        .rl-item-name.clickable:hover { color: var(--accent); text-decoration-color: var(--accent); }
        .rl-item-desc {
          font-size: var(--t-12); color: var(--ink-2);
          line-height: 1.5; margin-top: 4px; padding: 6px 0;
        }
        .rl-item-fn { font-size: var(--t-12); color: var(--ink-3); white-space: nowrap; margin-top: 2px; }

        /* Resource Insights Tooltip */
        .resource-insights-tooltip {
          position: fixed;
          z-index: 50;
          width: 300px;
          max-height: 320px;
          background: var(--surface);
          border: 1px solid var(--line);
          border-radius: var(--r2);
          box-shadow: var(--shadow-lg);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .rit-header {
          padding: var(--s8) var(--s12);
          border-bottom: 1px solid var(--line);
          background: var(--bg-inset);
        }
        .rit-title {
          font-size: var(--t-13);
          font-weight: 700;
          color: var(--ink-1);
          line-height: 1.3;
        }
        .rit-loading {
          padding: var(--s16);
          text-align: center;
          font-size: var(--t-12);
          color: var(--ink-3);
        }
        .rit-body {
          padding: var(--s8) var(--s12) var(--s12);
          overflow-y: auto;
          flex: 1;
        }
        .rit-stats {
          display: flex;
          gap: var(--s8);
          margin-bottom: var(--s8);
        }
        .rit-stat {
          flex: 1;
          text-align: center;
          padding: var(--s4) 0;
          background: var(--bg-inset);
          border-radius: var(--r1);
        }
        .rit-stat-value {
          font-size: var(--t-15);
          font-weight: 700;
          font-family: var(--font-mono);
          color: var(--ink-1);
          line-height: 1.2;
        }
        .rit-stat-label {
          font-size: 9px;
          font-weight: 500;
          color: var(--ink-3);
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .rit-stat-total {
          font-size: 8px;
          font-family: var(--font-mono);
          color: var(--ink-2);
          margin-top: 1px;
        }
        .rit-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 3px;
          margin-bottom: var(--s8);
        }
        .rit-tag {
          font-size: 9px;
          font-weight: 600;
          padding: 1px 6px;
          border-radius: var(--r-full);
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        .rit-tag-domain {
          background: var(--accent-dim);
          color: var(--accent);
        }
        .rit-tag-nature {
          background: var(--bg-inset);
          color: var(--ink-3);
        }
        .rit-cap-list {
          display: flex;
          flex-direction: column;
          gap: 1px;
        }
        .rit-cap-row {
          display: flex;
          align-items: center;
          gap: var(--s8);
          padding: 3px var(--s4);
          border-radius: var(--r1);
          font-size: var(--t-12);
        }
        .rit-cap-row:hover { background: var(--bg-inset); }
        .rit-cap-name {
          flex: 1;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: var(--ink-1);
        }
        .rit-cap-consensus {
          font-family: var(--font-mono);
          font-weight: 600;
          color: var(--ink-2);
          flex-shrink: 0;
        }

        /* Resource card tooltip for qualification modal */
        .modal-full .resource-card {
          position: relative;
        }
        .modal-full .resource-card-info {
          position: absolute;
          top: 4px;
          right: 4px;
          bottom: auto;
          opacity: 0;
        }
        .modal-full .resource-card:hover .resource-card-info {
          opacity: 1;
        }
        /* Portal-based tooltip - renders at document.body level */
        .resource-card-tooltip-portal {
          position: fixed;
          z-index: 99999;
          width: 250px;
          padding: var(--s12);
          background: #ffffff;
          border: 1px solid var(--line);
          border-radius: var(--r2);
          box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          font-size: var(--t-12);
          line-height: 1.5;
          text-align: left;
          pointer-events: none;
        }
        .resource-card-tooltip-title {
          font-weight: 600;
          font-size: var(--t-13);
          color: #1a1a1a;
          margin-bottom: var(--s4);
          line-height: 1.3;
        }
        .resource-card-tooltip-desc {
          color: #555555;
        }
        [data-theme="dark"] .resource-card-tooltip-portal {
          background: #2a2a2a;
          border-color: #444444;
          box-shadow: 0 8px 32px rgba(0,0,0,0.5);
        }
        [data-theme="dark"] .resource-card-tooltip-title {
          color: #f0f0f0;
        }
        [data-theme="dark"] .resource-card-tooltip-desc {
          color: #cccccc;
        }

        /* Unclassified Resources Section */
        .unclassified-resources-section {
          margin-top: var(--s20);
          padding-top: var(--s16);
          border-top: 1px dashed var(--line);
        }
        .unclassified-header {
          display: flex;
          align-items: center;
          gap: var(--s12);
          margin-bottom: var(--s4);
        }
        .unclassified-label {
          font-size: var(--t-13);
          font-weight: 600;
          color: var(--warn);
        }
        .unclassified-count {
          font-size: var(--t-12);
          font-family: var(--font-mono);
          color: var(--ink-2);
          background: var(--bg-inset);
          padding: 2px 8px;
          border-radius: var(--r1);
        }
        .unclassified-hint {
          font-size: var(--t-12);
          color: var(--ink-2);
          font-style: italic;
          margin-bottom: var(--s12);
        }
        .unclassified-grid {
          display: flex;
          flex-wrap: wrap;
          gap: var(--s8);
        }
        .unclassified-grid .resource-card {
          flex: 0 0 auto;
          min-width: 180px;
          max-width: 220px;
        }
      `}</style>

      <input
        type="file"
        ref={fileInputRef}
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleCsvFileSelect}
      />

      <div
        className={`matrix-grid-container${spaceHeld ? ' panning-ready' : ''}`}
        ref={gridContainerRef}
      >
        <div className="matrix-grid" style={{ minWidth: '1300px' }}>
          <div className="matrix-grid-corner"></div>

          {NATURE_ORDER.map(nature => (
            <div key={nature} className="matrix-grid-col">
              {nature}
            </div>
          ))}

          {DOMAIN_ORDER.map(domain => (
            <React.Fragment key={domain}>
              <div className="matrix-grid-row">{domain}</div>
              {NATURE_ORDER.map(nature => {
                const capabilitiesInCell = capabilityGrid[domain][nature];
                return (
                  <div key={nature} className="matrix-grid-cell">
                    {capabilitiesInCell.map(capability => (
                      <CapabilityCard
                        key={capability.id}
                        capability={capability}
                        onClick={() => setSelectedCapability(capability)}
                        onShowInfo={() => handleShowInfo(capability)}
                      />
                    ))}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Detail Panel */}
      {detailCapability && (
        <DetailPanel
          capability={detailCapability}
          onClose={() => setDetailCapability(null)}
        />
      )}

      {/* Resource Selection Modal */}
      {selectedCapability && (
        <ResourceGridModal
          capability={selectedCapability}
          onClose={() => {
            setSelectedCapability(null);
            loadCapabilities();
          }}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
}
